import { z } from 'zod/v4'
import type { Database } from '../db/supabase.types.ts'
import { phoneNumberSchema } from '../lib/phone.ts'
import type { RespondedQuestionnaireAnswers } from '../questionnaires/types.ts'

export const contactLanguageSchema = z.enum(['fi', 'en', 'es', 'sv'])
export type ContactLanguage = z.infer<typeof contactLanguageSchema>

export const newContactSchema = z.object({
  name: z.string().min(2, { message: 'Nimi on pakollinen' }),
  email: z.email().optional(),
  phone: phoneNumberSchema.optional(),
  consent_confirmed: z.literal(true),
  language: contactLanguageSchema.default('fi'),
}).refine((contactData) => !!contactData.email || !!contactData.phone, {
  message: 'Email or phone is required',
})

export type NewContactData = z.infer<typeof newContactSchema>

export const updateContactSchema = z.object({
  id: z.uuid(),
  name: z.string().optional(),
  email: z.email().optional().or(z.literal('')),
  phone: z.string().optional(),
  language: contactLanguageSchema.optional(),
})

export type UpdateContactPayload = z.infer<typeof updateContactSchema>

export const contactPrivateDataSchema = z.object({
  notes: z.string().nullish(),
})

export type ContactPrivateData = z.infer<typeof contactPrivateDataSchema>

export const contactDataSchema = z.object({})

export type ContactData = z.infer<typeof contactDataSchema>

export type ContactFeedbackInterest = {
  id: string
  data: Database['public']['Tables']['interests']['Row']['data']
  created_at: string
}

export type ContactFeedback = Omit<
  Database['public']['Tables']['feedback']['Row'],
  'deleted_at' | 'contact_id' | 'user_id' | 'current_nonce'
> & {
  interests?: ContactFeedbackInterest[] | null
}

export type RawContact = Database['public']['Tables']['contacts']['Row'] & {
  feedback?: ContactFeedback[] | null
}

export type ParsedContactFeedback = ContactFeedback

export type FeedbackRowWithAsset = ParsedContactFeedback & {
  created_at: string
  declined_at?: string | null
}

export type ParsedContact = Omit<RawContact, 'feedback'> & {
  feedback: ParsedContactFeedback[]
  assets: { name: string; id: string; created_at: string }[]
  answers: RespondedQuestionnaireAnswers
}

export type DisplayContact = {
  id: RawContact['id']
  name: RawContact['name']
  email?: RawContact['email']
  phone: RawContact['phone']
  language?: RawContact['language']
  created_at?: RawContact['created_at']
  disabled_at?: RawContact['disabled_at']
  disabled_reason?: RawContact['disabled_reason']
  feedback?: ParsedContactFeedback[]
  assets?: { name: string; id: string; created_at: string }[]
  answers: RespondedQuestionnaireAnswers
}
